import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from '../store/createStore';
import {
  applyCoupon,
  checkoutAddPlu,
  checkoutRemovePlu,
  getCheckout,
  updatePluQtyCheckout,
  uploadCheckout,
} from '../store/modules/checkout/actions';
import {
  cartProductProps,
  checkoutActionTypes,
} from '../store/modules/checkout/types';
import { useToast } from './toast';
import { useTranslation } from './useTranslation';
import { AddressSelectedProps, useCheckoutInfo } from './useCheckoutInfo';

interface checkoutFunctionsContextProps {
  handleCheckoutActions: (
    type: string,
    product_id: string,
    quantityOrServiceId: string,
    cartProduct?: cartProductProps[],
    card?: boolean,
    observation?: string,
  ) => void;
  handleUpdateCheckout: () => void;
  handlerGetCheckout: () => void;
  handleApplyCoupon: (coupon: string, errorMessage: string) => void;
}

interface CheckoutFunctionProviderProps {
  children: ReactNode;
}

const checkoutFunctionsContext = createContext(
  {} as checkoutFunctionsContextProps,
);

const CheckoutFunctionProvider: React.FC<CheckoutFunctionProviderProps> = ({
  children,
}: CheckoutFunctionProviderProps) => {
  const {
    isSignedIn,
    user: {
      data: { seller },
    },
  } = useSelector((state: StoreState) => state.auth);
  const { checkout } = useSelector((state: StoreState) => state.checkout);
  const dispatch = useDispatch();
  const { onSetMessage } = useToast();
  const { onTranslation: t } = useTranslation();
  const { onSetaddressSelect } = useCheckoutInfo();

  const handleCheckoutActions = useCallback(
    (
      type: string,
      product_id: string,
      quantityOrServiceId: string | number,
      cartProduct?: cartProductProps[],
      card?: boolean,
      observation?: string,
    ) => {
      if (!isSignedIn) {
        onSetMessage({
          type: 'error',
          description: t('main.messages_toast.login'),
        });
        return;
      }

      const cartItem = checkout.items[product_id];
      onSetaddressSelect({} as AddressSelectedProps);

      switch (type) {
        case checkoutActionTypes.ADD_PLU_CHECKOUT: {
          const newCart = [] as cartProductProps[];
          const customQty =
            typeof quantityOrServiceId === 'string' ? quantityOrServiceId : '1';
          const serviceId =
            typeof quantityOrServiceId === 'number'
              ? quantityOrServiceId
              : undefined;
          if (cartProduct) {
            cartProduct.forEach(product => {
              newCart.push({
                ...product,
                [`${seller || undefined}`]: seller || undefined,
                [`${serviceId || undefined}`]: serviceId || undefined,
              });
            });
          } else {
            newCart.push({
              product_id: `${product_id}`,
              quantity: customQty,
              ...(seller ? { [`${seller}`]: `${seller}` } : {}),
              ...(serviceId ? { service_id: `${serviceId}` } : {}),
              observation,
            });
          }
          dispatch(
            checkoutAddPlu(
              product_id,
              seller || undefined,
              serviceId,
              newCart,
              observation,
            ),
          );
          break;
        }
        case checkoutActionTypes.REMOVE_PLU_CHECKOUT: {
          if (cartItem) {
            dispatch(
              checkoutRemovePlu(cartItem.cart_item_id),
            );
          }
          break;
        }
        case checkoutActionTypes.UPDATE_PLU_QTY_CHECKOUT: {
          let qtyValue: string;
          if (typeof quantityOrServiceId === 'string') {
            // Usa o valor passado (decimal ou inteiro)
            qtyValue = quantityOrServiceId;
          } else if (cartItem) {
            qtyValue = (Number(cartItem.quantity) - 1).toString();
          } else {
            qtyValue = '0';
          }
          if (cartItem) {
            dispatch(
              updatePluQtyCheckout(
                cartItem.cart_item_id,
                qtyValue,
                observation
              ),
            );
          }
          break;
        }
        default:
          console.warn(`Unhandled action type: ${type}`);
          break;
      }
    },
    [dispatch, checkout, isSignedIn, onSetMessage, t, seller, onSetaddressSelect],
  );

  const handleUpdateCheckout = useCallback(() => {
      dispatch(uploadCheckout());
  }, [dispatch]);

  const handlerGetCheckout = useCallback(() => {
    dispatch(getCheckout());
  }, [dispatch]);

  const handleApplyCoupon = useCallback(
    (coupon: string, errorMessage: string) => {
      dispatch(applyCoupon(coupon, errorMessage));
    },
    [dispatch],
  );

  return (
    <checkoutFunctionsContext.Provider
      value={{ 
        handleCheckoutActions, 
        handleUpdateCheckout, 
        handleApplyCoupon,
        handlerGetCheckout
      }}
    >
      {children}
    </checkoutFunctionsContext.Provider>
  );
};

function useCheckoutFunction(): checkoutFunctionsContextProps {
  const context = useContext(checkoutFunctionsContext);

  if (!context) {
    throw new Error(
      'checkoutFunctionsContext must be used within a useCheckoutFunction',
    );
  }

  return context;
}

export { CheckoutFunctionProvider, useCheckoutFunction };
